<script>
// Host do tour guiado: escurece a tela, abre o furo de luz sobre o elemento
// real do passo, posiciona o balão e leva o cursor até lá.
//
// Genérico de propósito — recebe passos prontos e emite intenções. Não conhece
// a Jornada de Configuração nem nenhum módulo: quem casa catálogo com seletores
// é a casca de integração (central-config), e quem tem os seletores é o módulo.
//
// Montado UMA vez, permanentemente, fora das views (main.vue) — seis das treze
// etapas do Fee Fixo navegam entre telas no meio do fluxo, e um host montado
// numa view morreria no router.push.
import { anchoredTargetMixin } from '@modules/global/mixins/anchored-target-mixin'
import GuidedTourCard from './guided-tour-card.vue'
import GuidedTourCursor from './guided-tour-cursor.vue'

// Folga entre o furo e o balão, e margem mínima até a borda da viewport.
const ESPACO_ATE_O_CARD = 14
const MARGEM_DA_VIEWPORT = 8
// Estimativa usada só no primeiro frame, antes de o card publicar a medida real.
const CARD_LARGURA_ESTIMADA = 320
const CARD_ALTURA_ESTIMADA = 220
// Respiro do furo em volta do alvo.
const FOLGA_DO_FURO = 6

// Abrir um modal produz dezenas de mutações em sequência; sem represar, cada nó
// inserido custaria uma bateria de `elementFromPoint`.
const DEBOUNCE_DE_REAVALIACAO = 120

// Frações do alvo onde a cobertura é amostrada. Só o centro reprova um alvo
// grande cujo meio caiu sob o balão — e como "coberto" centraliza o balão, o
// centro seguiria ocupado para sempre.
const FRACOES_DE_AMOSTRA = [0.5, 0.12, 0.88]

const FASE_DESFECHO = 'desfecho'
const CAMPOS_DE_TEXTO = ['INPUT', 'TEXTAREA', 'SELECT']

// O que responde a clique. `data-tour` cai no COMPONENTE, que quase sempre é um
// wrapper — quem tem o handler é um filho.
const TAGS_ACIONAVEIS = ['BUTTON', 'A', 'INPUT']
const SELETOR_ACIONAVEL = 'button, a, [role="button"], input'

export default {
  name: 'GuidedTourHost',

  components: { GuidedTourCard, GuidedTourCursor },

  mixins: [anchoredTargetMixin],

  props: {
    ativo: {
      type: Boolean,
      default: false,
    },

    // [{ titulo, detalhe, alvo, avancaNoClique }] — `alvo` null cai no balão
    // centralizado (passo sem elemento ancorável).
    passos: {
      type: Array,
      default: () => [],
    },

    indice: {
      type: Number,
      default: 0,
    },

    fase: {
      type: String,
      default: 'passos',
    },

    proximaEtapa: {
      type: Object,
      default: null,
    },

    marcando: {
      type: Boolean,
      default: false,
    },

    // Só repassado ao card, para o rótulo do "Ver/Ocultar passo a passo". O
    // host não sabe o que é o drawer — quem sabe é a casca de integração.
    // Default `false` por exigência do lint (vue/no-boolean-default).
    drawerAberto: {
      type: Boolean,
      default: false,
    },

    // Default `false` por exigência do lint (vue/no-boolean-default) — quem
    // manda de verdade é o slice do tour, que nasce com a marcação LIGADA.
    marcarConcluida: {
      type: Boolean,
      default: false,
    },

    // Incrementar força uma nova tentativa de ancoragem. A janela de retry
    // própria (~2,4s) cobre "o alvo ainda está montando"; não cobre "o alvo só
    // vai existir quando o usuário abrir o modal", que pode levar minutos.
    // Quem sabe que a hora chegou é a casca de integração, ouvindo o GlobalBus.
    reancorarToken: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      medidaDoCard: null,
      cliqueEl: null,
      cliqueHandler: null,
      tecladoHandler: null,
      // Qual dos `alvoAoAbrir` do passo está desenhado agora (`null` = nenhum,
      // e o passo fica no alvo base). Reavaliado a cada reancorarToken, a cada
      // troca de passo, e sempre que o DOM ou uma animação mexem na tela.
      alvoAoAbrirResolvido: null,
      // O alvo existe mas está DEBAIXO de outra coisa (um modal abriu por
      // cima). Furo sobre elemento coberto desenha um retângulo de luz no
      // vazio — pior que não apontar nada.
      alvoEstaCoberto: false,
      // Incrementado quando o alvo se repete entre passos: sem mudança de
      // ponto o cursor não se moveria, e o passo passaria despercebido.
      clickToken: 0,
      // Observa o DOM enquanto o tour está no ar. `reancorarToken` cobre só o
      // que o app ANUNCIA (o canal do modal); o observador cobre o resto —
      // fechar o modal, expandir uma seção, a lista terminar de carregar.
      observadorDeDom: null,
      reavaliacaoAgendada: null,
      fimDeAnimacaoHandler: null,
    }
  },

  computed: {
    // O host é permanente: portaliza no mount mesmo com o tour inativo.
    anchorDevePortalizar() {
      return true
    },

    passoAtual() {
      if (!this.ativo) return null
      return this.passos[this.indice] || null
    },

    // No desfecho não há alvo — o balão fica centralizado sobre a tela inteira.
    //
    // Um passo pode ter DOIS alvos: o de sempre (`alvo`) e o que só passa a
    // existir depois de o usuário abrir algo (`alvoAoAbrir`, tipicamente dentro
    // de um modal). Enquanto o segundo não existe, ancora no primeiro — assim o
    // passo aponta o botão que abre, em vez de não apontar nada.
    anchorSelector() {
      if (this.noDesfecho) return ''
      const passo = this.passoAtual
      if (!passo) return ''
      if (this.alvoAoAbrirResolvido) return this.alvoAoAbrirResolvido
      return passo.alvo || ''
    },

    // `alvoAoAbrir` aceita um seletor ou uma LISTA deles, em ordem de fluxo.
    // Uma lista é o que cobre o passo cuja tela troca de conteúdo no meio: subir
    // planilha começa na dropzone e termina no "Confirmar importação", que só
    // existe depois — e aí a dropzone já saiu do DOM. Com um alvo só, o furo
    // sumia justamente na hora de concluir a ação (medido em tela, 14/08).
    alvosAoAbrir() {
      const passo = this.passoAtual
      const bruto = (passo && passo.alvoAoAbrir) || null
      if (!bruto) return []
      return Array.isArray(bruto) ? bruto : [bruto]
    },

    // Passo cuja conclusão é um EVENTO do app (`avancaEm`), não um clique no
    // balão: confirmar importação, gerar relatório. O "Próximo" some nesses,
    // porque adiantar o balão levaria a um passo que descreve uma tela ainda
    // não produzida — e o tour não pode disparar a ação por conta própria, já
    // que ela GRAVA (escopo fechado com o owner em 14/08).
    passoConcluiPorAcao() {
      if (this.noDesfecho) return false
      const passo = this.passoAtual
      return !!(passo && passo.avancaEm)
    },

    anchorActive() {
      return this.ativo && !!this.anchorSelector
    },

    noDesfecho() {
      return this.ativo && this.fase === FASE_DESFECHO
    },

    total() {
      return this.passos.length
    },

    tituloDoPasso() {
      const passo = this.passoAtual
      return (passo && passo.titulo) || ''
    },

    detalheDoPasso() {
      const passo = this.passoAtual
      return (passo && passo.detalhe) || ''
    },

    // Alvo dentro de um v-dialog já vive sob o scrim do próprio Vuetify
    // (rgba(33,33,33,0.46)). Somar o nosso por cima dá ~0.75 de alpha e o card
    // do modal fica ilegível — o furo perde a função. Aí usamos o scrim suave,
    // que composto com o do dialog dá aproximadamente o mesmo tom do caso normal.
    dentroDeModal() {
      if (!this.targetEl || !this.targetEl.closest) return false
      return !!this.targetEl.closest('.v-dialog__content')
    },

    corDoScrim() {
      return this.dentroDeModal ? 'var(--tour-scrim-soft)' : 'var(--tour-scrim)'
    },

    mostraFuro() {
      if (!this.ativo || this.noDesfecho) return false
      if (!this.targetRect || this.alvoEstaCoberto) return false
      return this.rectTemArea
    },

    /**
     * O rect tem área desenhável?
     *
     * Alvo PRESENTE mas não renderizado (seção colapsada, aba inativa, v-dialog
     * fechado) devolve rect 0×0 — e o furo virava um retângulo degenerado no
     * canto, com o cursor apontando um ponto que não existe. É diferente do
     * caso "alvo ausente", que o `anchorSelector` já resolve devolvendo vazio:
     * aqui o elemento existe, o mixin o encontra, e o rect mente.
     *
     * Visto em tela (14/08) em `ensaiar-conversa-roleplay`: `ff-ensaio-tom`
     * existe na tela do ensaio, mas dentro de um bloco recolhido.
     *
     * Sem área ⇒ sem furo ⇒ o balão centraliza, que é o mesmo desfecho honesto
     * de um passo sem alvo.
     */
    rectTemArea() {
      const r = this.targetRect
      return !!r && r.width > 0 && r.height > 0
    },

    // Sem alvo resolvido (passo sem elemento, alvo ainda montando, ou desfecho)
    // a tela inteira escurece — senão o usuário veria o balão sobre a tela clara
    // e não saberia que o tour está no ar.
    estiloDoVeu() {
      if (!this.ativo || this.mostraFuro) return null
      return {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100vw',
        height: '100vh',
        background: 'var(--tour-scrim)',
        'pointer-events': 'none',
        'z-index': '260',
      }
    },

    estiloDoFuro() {
      const rect = this.targetRect
      if (!rect) return null
      return {
        position: 'fixed',
        top: `${rect.top - FOLGA_DO_FURO}px`,
        left: `${rect.left - FOLGA_DO_FURO}px`,
        width: `${rect.width + FOLGA_DO_FURO * 2}px`,
        height: `${rect.height + FOLGA_DO_FURO * 2}px`,
        'box-shadow': `0px 0px 0px 3000px ${this.corDoScrim}`,
        'border-radius': 'var(--radius-sm)',
        // O furo não pode capturar o clique: no modo híbrido ele precisa
        // chegar ao elemento real que está embaixo.
        'pointer-events': 'none',
        'z-index': '260',
        transition:
          'top var(--motion-duration-default) var(--motion-easing-standard), left var(--motion-duration-default) var(--motion-easing-standard), width var(--motion-duration-default) var(--motion-easing-standard), height var(--motion-duration-default) var(--motion-easing-standard)',
      }
    },

    larguraDoCard() {
      const medida = this.medidaDoCard
      return medida && medida.width ? medida.width : CARD_LARGURA_ESTIMADA
    },

    alturaDoCard() {
      const medida = this.medidaDoCard
      return medida && medida.height ? medida.height : CARD_ALTURA_ESTIMADA
    },

    estiloDoCard() {
      const base = {
        position: 'fixed',
        'z-index': '262',
        'pointer-events': 'auto',
      }
      // `rectTemArea` entra aqui junto com os outros: rect 0×0 posicionaria o
      // balão no canto superior esquerdo, colado num alvo que não se vê.
      if (
        !this.targetRect ||
        this.noDesfecho ||
        this.alvoEstaCoberto ||
        !this.rectTemArea
      ) {
        return {
          ...base,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }
      }
      const pos = this.posicionarCard(this.targetRect)
      return { ...base, top: `${pos.top}px`, left: `${pos.left}px` }
    },

    // A ponta da seta do cursor aponta o centro do alvo.
    pontoDoCursor() {
      const rect = this.targetRect
      if (!rect || this.alvoEstaCoberto) return null
      // Mesmo motivo do furo: rect 0×0 é alvo não desenhado, e o cursor iria
      // apontar um ponto que não existe na tela.
      if (!this.rectTemArea) return null
      return {
        x: Math.round(rect.left + rect.width / 2),
        y: Math.round(rect.top + rect.height / 2),
      }
    },

    mostraCursor() {
      return this.ativo && !this.noDesfecho && !!this.pontoDoCursor
    },
  },

  watch: {
    ativo: 'sincronizarEfeitos',
    indice: 'aoTrocarDePasso',
    targetEl: 'aoResolverAlvo',
    targetRect: 'verificarCobertura',

    reancorarToken() {
      this.avaliarAlvoAoAbrir()
      if (this.anchorActive) this.scheduleRectUpdate()
    },
  },

  mounted() {
    this.sincronizarEfeitos()
  },

  beforeDestroy() {
    this.desligarCliqueNoAlvo()
    this.desligarTeclado()
    this.desligarObservadorDeDom()
    this.desligarFimDeAnimacao()
  },

  methods: {
    // Abaixo do alvo por padrão; acima quando não cabe; sempre preso à
    // viewport. Usa a altura MEDIDA do card — o Tour legado usa 180 cravado, e
    // um detalhe mais longo vaza da tela sem ninguém perceber.
    posicionarCard(rect) {
      const largura = this.larguraDoCard
      const altura = this.alturaDoCard

      let top = rect.bottom + ESPACO_ATE_O_CARD
      if (top + altura > window.innerHeight) {
        top = rect.top - altura - ESPACO_ATE_O_CARD
      }
      if (top < MARGEM_DA_VIEWPORT) top = MARGEM_DA_VIEWPORT

      let left = rect.left
      const maxLeft = window.innerWidth - largura - MARGEM_DA_VIEWPORT
      if (left > maxLeft) left = maxLeft
      if (left < MARGEM_DA_VIEWPORT) left = MARGEM_DA_VIEWPORT

      return { top, left }
    },

    aoMedirCard(medida) {
      this.medidaDoCard = medida
    },

    // O alvo existe no DOM, mas o usuário consegue VÊ-LO? Quando um modal abre
    // por cima, o rect continua válido e o furo desenharia um retângulo de luz
    // sobre o overlay — apontando o nada (visto em tela em 14/08, no tour do Fee
    // Fixo: um passo ancorado no botão "Importar clientes" seguia com o furo lá
    // depois que o modal de importação cobriu o botão).
    // Coberto ⇒ sem furo, sem cursor, balão centralizado: o passo continua
    // legível e para de mentir sobre onde olhar.
    //
    // Roda no watch de `targetRect` E na reavaliação por mutação do DOM: abrir
    // ou fechar um modal não move o alvo, então só o rect nunca reavaliaria — o
    // veredito ficava preso, tanto escondendo furo de alvo visível quanto
    // desenhando furo sobre alvo coberto.
    verificarCobertura() {
      const rect = this.targetRect
      const el = this.targetEl
      if (!rect || !el || !document.elementFromPoint) {
        this.alvoEstaCoberto = false
        return
      }
      this.alvoEstaCoberto = !this.algumPontoAlcancaOAlvo(rect, el)
    },

    // Basta UM ponto amostrado chegar ao alvo para ele estar visível.
    //
    // O balão é descartado da amostra, e essa é a parte que não pode ser
    // simplificada: ao contrário do véu e do furo, ele é `pointer-events: auto`
    // (tem botões), então responde ao `elementFromPoint`. Contá-lo fazia o
    // próprio tour declarar o alvo coberto — e como coberto centraliza o balão,
    // ele passava a ocupar o centro da tela, que é justamente onde os alvos
    // largos têm o meio: o estado se auto-alimentava e o furo nunca voltava.
    algumPontoAlcancaOAlvo(rect, el) {
      let houveAmostraUtil = false
      for (const fx of FRACOES_DE_AMOSTRA) {
        for (const fy of FRACOES_DE_AMOSTRA) {
          const noTopo = document.elementFromPoint(
            rect.left + rect.width * fx,
            rect.top + rect.height * fy
          )
          if (!noTopo) continue
          if (this.$el && this.$el.contains(noTopo)) continue
          houveAmostraUtil = true
          if (el === noTopo || el.contains(noTopo) || noTopo.contains(el)) {
            return true
          }
        }
      }
      // Só o nosso overlay respondeu em todos os pontos: nada de terceiros está
      // por cima, então não há cobertura a declarar.
      return !houveAmostraUtil
    },

    // Um pulso de mutação pode ter montado o alvo que faltava, removido o que
    // cobria, ou mudado o tamanho de quem já estava lá — reavalia os três.
    reavaliarAncoragem() {
      if (!this.ativo) return
      this.avaliarAlvoAoAbrir()
      if (this.anchorActive) this.scheduleRectUpdate()
      this.verificarCobertura()
    },

    agendarReavaliacao() {
      if (this.reavaliacaoAgendada) return
      this.reavaliacaoAgendada = setTimeout(() => {
        this.reavaliacaoAgendada = null
        this.reavaliarAncoragem()
      }, DEBOUNCE_DE_REAVALIACAO)
    },

    // Uma transição CSS termina SEM tocar o DOM: o navegador interpola no
    // compositor, e o `MutationObserver` não vê nada. Sem isto, o passo que
    // começa junto com a abertura de um modal mede o alvo em pleno voo e o furo
    // fica onde o botão passou, não onde ele parou (medido em tela, 14/08: 648px
    // de desvio, permanentes, no passo "baixe o modelo").
    ligarFimDeAnimacao() {
      if (this.fimDeAnimacaoHandler) return
      this.fimDeAnimacaoHandler = () => this.agendarReavaliacao()
      document.addEventListener(
        'transitionend',
        this.fimDeAnimacaoHandler,
        true
      )
      document.addEventListener('animationend', this.fimDeAnimacaoHandler, true)
    },

    desligarFimDeAnimacao() {
      if (!this.fimDeAnimacaoHandler) return
      document.removeEventListener(
        'transitionend',
        this.fimDeAnimacaoHandler,
        true
      )
      document.removeEventListener(
        'animationend',
        this.fimDeAnimacaoHandler,
        true
      )
      this.fimDeAnimacaoHandler = null
    },

    ligarObservadorDeDom() {
      if (this.observadorDeDom || typeof MutationObserver === 'undefined')
        return
      this.observadorDeDom = new MutationObserver((mutacoes) => {
        // Mutação que só toca o próprio overlay é eco do nosso render: reagir a
        // ela reposicionaria o balão, que dispara outra mutação.
        const deTerceiros = mutacoes.some(
          (m) => !this.$el || !this.$el.contains(m.target)
        )
        if (deTerceiros) this.agendarReavaliacao()
      })
      this.observadorDeDom.observe(document.body, {
        childList: true,
        subtree: true,
        // `style`/`class` também, porque o v-dialog ENTRA animado: os nós já
        // existem enquanto o modal ainda desliza, e medir aí grava uma posição
        // intermediária que nunca mais é corrigida (o retry do mixin encerra
        // assim que ACHA o elemento, não quando ele para de se mexer). Medido
        // em 14/08: o furo do "baixar o modelo" ficou 160px à direita do botão.
        // Transição CSS não gera mutação por frame — só a troca de classe no
        // início e no fim —, e o debounce faz a última medição valer.
        attributes: true,
        attributeFilter: ['style', 'class'],
      })
    },

    desligarObservadorDeDom() {
      if (this.observadorDeDom) this.observadorDeDom.disconnect()
      this.observadorDeDom = null
      if (this.reavaliacaoAgendada) clearTimeout(this.reavaliacaoAgendada)
      this.reavaliacaoAgendada = null
    },

    // Um passo novo começa sempre pelo alvo base; o alternativo só entra quando
    // o app avisar que abriu o que faltava.
    //
    // PRESENTE não basta, tem de estar RENDERIZADO: o v-dialog do Vuetify não
    // desmonta ao fechar — o nó continua no documento com rect 0×0. Aceitando só
    // o `querySelector`, o passo ficava ancorado para sempre num alvo de dentro
    // do modal fechado, e o balão centralizava sem apontar nada em vez de voltar
    // ao botão que reabre a janela (medido em tela, 14/08).
    avaliarAlvoAoAbrir() {
      const candidatos = this.alvosAoAbrir
      for (const candidato of candidatos) {
        const el = document.querySelector(candidato)
        if (el && this.estaRenderizado(el)) {
          this.alvoAoAbrirResolvido = candidato
          return
        }
      }
      this.alvoAoAbrirResolvido = null
    },

    // `getClientRects()` vazio é o teste canônico de "não desenhado" — pega
    // `display: none` no próprio elemento e em qualquer ancestral, que é como
    // o modal fechado apaga o conteúdo sem tirá-lo do DOM.
    estaRenderizado(el) {
      if (!el.getClientRects) return true
      return el.getClientRects().length > 0
    },

    aoTrocarDePasso() {
      // Zera o que era do passo ANTERIOR e RECONSULTA o DOM na sequência.
      //
      // Só zerar deixava o passo novo cego ao que já está na tela: quando o
      // modal continua aberto de um passo para o outro, não há mutação nova
      // para o observador pegar, e um passo com `alvoAoAbrir` nascia `false`
      // para sempre — foi assim que "suba a planilha preenchida" deixou de
      // apontar a dropzone que estava bem ali, visível (medido em tela, 14/08).
      this.alvoAoAbrirResolvido = null
      // O veredito de cobertura é do alvo ANTERIOR. Herdá-lo esconde o furo de
      // um passo cujo alvo está à vista — e, como nada o recalcula sozinho, ele
      // atravessa o resto do tour.
      this.alvoEstaCoberto = false
      this.avaliarAlvoAoAbrir()
      // Alvo repetido entre passos consecutivos não move o cursor; o pulso é o
      // que sinaliza que o passo avançou.
      this.clickToken += 1
      this.ligarCliqueNoAlvo()
    },

    sincronizarEfeitos() {
      if (this.ativo) {
        // ★ O veredito de cobertura e o alvo resolvido são da execução
        // ANTERIOR do tour, e sobrevivem porque o host não desmonta.
        //
        // `aoTrocarDePasso` já limpava isso, mas ele roda no watch de `indice`
        // — e um tour que ABRE no passo 0, vindo de um que terminou no 0,
        // não muda o índice, então o watch nunca dispara. O tour novo começava
        // herdando "alvo coberto" de quando o modal do tour anterior estava
        // aberto, e o primeiro passo não abria mais nada: o "Próximo" via
        // `alvoEstaCoberto` e só adiantava o balão (medido em tela, 14/08).
        //
        // É a mesma classe de bug que a troca de passo já tratava, na fronteira
        // que faltava: a de ATIVAÇÃO.
        this.alvoEstaCoberto = false
        this.alvoAoAbrirResolvido = null
        this.avaliarAlvoAoAbrir()
        this.ligarTeclado()
        this.ligarCliqueNoAlvo()
        this.ligarObservadorDeDom()
        this.ligarFimDeAnimacao()
        return
      }
      this.desligarTeclado()
      this.desligarCliqueNoAlvo()
      this.desligarObservadorDeDom()
      this.desligarFimDeAnimacao()
      this.medidaDoCard = null
    },

    // O alvo acabou de ser resolvido (ou trocou): religa o clique e garante que
    // ele esteja VISÍVEL.
    aoResolverAlvo() {
      this.ligarCliqueNoAlvo()
      this.garantirAlvoVisivel()
    },

    /**
     * Rola até o alvo quando ele está fora da viewport.
     *
     * Sem isto o tour descreve um elemento que o usuário não vê: em
     * `simular-migracao-cliente`, o passo "Compare os três regimes" aponta um
     * alvo em `top: 792` numa viewport de 720 — abaixo da dobra, com
     * `scrollY: 0`. O balão aparece, o furo desenha em outro lugar, e a
     * instrução fala de algo fora da tela (medido em tela, 14/08; 5 passos do
     * módulo no mesmo caso).
     *
     * `block: 'center'` em vez de `'nearest'`: alvo grande (o de regimes tem
     * 265px) encostado na borda ficaria tecnicamente "visível" e visualmente
     * espremido, sem espaço para o balão, que é posicionado logo abaixo dele.
     *
     * Rolagem SUAVE é segura aqui porque o mixin de ancoragem já escuta
     * `scroll` em capture — o rect é remedido durante a rolagem e o furo
     * acompanha em vez de ficar para trás.
     *
     * Só rola quando precisa: rolar a cada passo tiraria o usuário do lugar
     * onde ele já estava olhando.
     */
    garantirAlvoVisivel() {
      const el = this.targetEl
      if (!this.ativo || !el || !el.scrollIntoView) return
      if (!this.precisaRolarAte(el)) return
      el.scrollIntoView({
        block: 'center',
        inline: 'nearest',
        behavior: 'smooth',
      })
    },

    precisaRolarAte(el) {
      if (!el.getBoundingClientRect) return false
      const r = el.getBoundingClientRect()
      // Alvo sem caixa (não renderizado) não é caso de rolagem — quem trata é
      // o `alvoAoAbrir`/retry do mixin.
      if (!r.width && !r.height) return false
      return r.top < 0 || r.bottom > window.innerHeight
    },

    // MODO HÍBRIDO: o clique real no elemento destacado avança o tour, sem
    // sequestrar o handler original. Capture (para ver o evento antes do
    // handler do app) + zero preventDefault (a ação precisa acontecer de
    // verdade) + avanço adiado um macrotask.
    //
    // O adiamento é o que faz a etapa "clique em Simular" funcionar: o mesmo
    // clique navega E avança. Avançar de forma síncrona mediria o alvo do
    // passo seguinte antes de a rota trocar.
    ligarCliqueNoAlvo() {
      this.desligarCliqueNoAlvo()
      const passo = this.passoAtual
      if (!this.ativo || !this.targetEl || !passo) return
      if (passo.avancaNoClique === false) return

      const indiceNoMomento = this.indice
      this.cliqueEl = this.targetEl
      this.cliqueHandler = () => {
        setTimeout(() => this.avancarSeAindaNoPasso(indiceNoMomento), 0)
      }
      this.cliqueEl.addEventListener('click', this.cliqueHandler, true)
    },

    desligarCliqueNoAlvo() {
      if (this.cliqueEl && this.cliqueHandler) {
        this.cliqueEl.removeEventListener('click', this.cliqueHandler, true)
      }
      this.cliqueEl = null
      this.cliqueHandler = null
    },

    // Idempotente por passo: "Próximo" e o clique real podem disparar juntos,
    // e o segundo tem de ser no-op.
    avancarSeAindaNoPasso(indiceEsperado) {
      if (!this.ativo || indiceEsperado !== this.indice) return
      this.$emit('avancar')
    },

    /**
     * "Próximo" FAZ a ação de abertura que o passo pede, quando ela ainda não
     * foi feita.
     *
     * Sem isto, o balão e a tela descolavam: o usuário lia "abra o importador",
     * clicava em Próximo esperando que o tour o levasse, e caía no passo 2 —
     * que fala de um botão dentro de uma janela que continuava fechada. Daí em
     * diante cada passo descrevia uma tela que não estava na frente dele
     * (relatado em tela, 14/08).
     *
     * ⚠️ ESCOPO FECHADO em abrir/expandir/navegar (decisão do owner, 14/08):
     * ações idempotentes e sem efeito no servidor. Baixar o modelo, confirmar
     * importação, guardar cenário e encerrar ensaio continuam exigindo o clique
     * do usuário — um tour que grava ou baixa por conta própria assusta mais do
     * que ajuda. Quem declara é o mapa do módulo, em `abreNoProximo`; passo sem
     * a marca só avança o balão, como sempre.
     *
     * O rótulo do botão NÃO muda (decisão do owner): é sempre "Próximo".
     */
    aoPedirProximo() {
      if (!this.acaoDeAberturaPendente()) {
        this.$emit('avancar')
        return
      }
      // O clique real dispara o handler do app; o `ligarCliqueNoAlvo` já escuta
      // esse mesmo clique em capture e avança um macrotask depois — deixar ELE
      // avançar é o que evita avançar duas vezes num passo com `avancaNoClique`.
      this.elementoAcionavelDoAlvo().click()
      if (this.passoAtual && this.passoAtual.avancaNoClique === false) {
        setTimeout(() => this.avancarSeAindaNoPasso(this.indice), 0)
      }
    },

    /**
     * O que de fato responde a um clique dentro do alvo.
     *
     * `data-tour` costuma cair no COMPONENTE, e o componente costuma ser um
     * wrapper: `ff-importar-split` é uma `<div class="az-split">` com dois
     * `<button>` dentro (o principal e a seta). `div.click()` dispara um evento
     * na div — o listener do tour captura e avança o balão — mas o `@click` do
     * Vue mora no `<button>`, então a ação do app NUNCA acontece. O sintoma é
     * cruel porque o tour parece funcionar: o passo avança e a janela não abre,
     * e o passo 2 passa a descrever algo que não está na tela (visto em tela,
     * 14/08).
     *
     * O PRIMEIRO acionável é o certo aqui: num split button ele é a ação
     * principal, e a seta (que abre o menu secundário) é a segunda.
     */
    elementoAcionavelDoAlvo() {
      const el = this.targetEl
      if (!el) return null
      if (this.ehAcionavel(el)) return el
      return el.querySelector(SELETOR_ACIONAVEL) || el
    },

    ehAcionavel(el) {
      if (!el.tagName) return false
      if (TAGS_ACIONAVEIS.includes(el.tagName)) return true
      return el.getAttribute && el.getAttribute('role') === 'button'
    },

    /**
     * A abertura que este passo pede ainda está por fazer?
     *
     * O sinal é o alvo do passo SEGUINTE: se ele já está renderizado, a janela
     * (ou a seção) que este passo manda abrir está aberta — clicar de novo a
     * fecharia. Derivar do passo seguinte evita repetir o seletor no mapa: ele
     * já está lá, uma linha abaixo.
     */
    acaoDeAberturaPendente() {
      const passo = this.passoAtual
      if (!passo || !passo.abreNoProximo || !this.targetEl) return false
      if (this.alvoEstaCoberto) return false

      const seletor = this.seletorDoPassoSeguinte()
      if (!seletor) return false
      const el = document.querySelector(seletor)
      return !el || !this.estaRenderizado(el)
    },

    // O que o passo seguinte aponta: o alvo de dentro do que abre, se houver
    // (é ele que só existe depois da abertura), senão o alvo base.
    seletorDoPassoSeguinte() {
      const proximo = this.passos[this.indice + 1]
      if (!proximo) return ''
      const aoAbrir = proximo.alvoAoAbrir
      if (Array.isArray(aoAbrir)) return aoAbrir[0] || ''
      return aoAbrir || proximo.alvo || ''
    },

    // O foco não pode viver no balão: o VDialog do Vuetify puxa o foco de volta
    // para dentro do modal (o `getMaxZIndex` dele só enxerga elementos do
    // próprio Vuetify, então nosso nó portalizado é invisível para ele e
    // z-index não resolve). Com o teclado no document, o balão é operável sem
    // nunca receber foco.
    ligarTeclado() {
      if (this.tecladoHandler) return
      this.tecladoHandler = (evento) => this.aoTeclar(evento)
      document.addEventListener('keydown', this.tecladoHandler, true)
    },

    desligarTeclado() {
      if (!this.tecladoHandler) return
      document.removeEventListener('keydown', this.tecladoHandler, true)
      this.tecladoHandler = null
    },

    aoTeclar(evento) {
      if (!this.ativo || this.digitandoEm(evento.target)) return
      if (evento.key === 'Escape') {
        this.$emit('fechar')
        return
      }
      if (this.noDesfecho) return
      if (evento.key === 'ArrowRight') {
        this.$emit('avancar')
        return
      }
      if (evento.key === 'ArrowLeft' && this.indice > 0) {
        this.$emit('voltar')
      }
    },

    digitandoEm(alvo) {
      if (!alvo || !alvo.tagName) return false
      if (CAMPOS_DE_TEXTO.includes(alvo.tagName)) return true
      return alvo.isContentEditable === true
    },
  },
}
</script>

<template>
  <div v-if="ativo" class="gt-host">
    <div v-if="estiloDoVeu" class="gt-host__veu" :style="estiloDoVeu"></div>

    <div v-if="mostraFuro" class="gt-host__furo" :style="estiloDoFuro"></div>

    <GuidedTourCursor
      :point="pontoDoCursor"
      :visible="mostraCursor"
      :click-token="clickToken"
    />

    <div class="gt-host__card" :style="estiloDoCard">
      <GuidedTourCard
        :titulo="tituloDoPasso"
        :detalhe="detalheDoPasso"
        :indice="indice"
        :total="total"
        :fase="fase"
        :proxima-etapa="proximaEtapa"
        :marcar-concluida="marcarConcluida"
        :marcando="marcando"
        :drawer-aberto="drawerAberto"
        :aguarda-acao="passoConcluiPorAcao"
        @medido="aoMedirCard"
        @proximo="aoPedirProximo"
        @anterior="$emit('voltar')"
        @fechar="$emit('fechar')"
        @alternar-drawer="$emit('alternar-drawer')"
        @proxima-etapa="$emit('proxima-etapa')"
        @update:marcarConcluida="$emit('update:marcarConcluida', $event)"
      />
    </div>
  </div>
</template>

<style lang="sass" scoped>
.gt-host
  position: fixed
  top: 0
  left: 0
  width: 100vw
  height: 100vh
  // O overlay inteiro é transparente ao mouse; só o balão recebe cliques. É o
  // que permite o modo híbrido — e também significa que o usuário pode clicar
  // em qualquer lugar da tela durante o tour, de propósito: o tour orienta,
  // não aprisiona.
  pointer-events: none
  z-index: 260
</style>
